import React, { FC, useEffect, useId, useRef, useState } from 'react'
import cx from 'classnames'
import { useButton } from 'react-aria'
import { easeInOut, useAnimate } from 'motion/react'

import { RichTextProps } from '../RichText'
import Illustration, { IllustrationProps } from '../Illustration'
import VideoSingle, { VideoSingleProps } from '../VideoSingle'
import { Icons } from '../Icon'
import ActionButton, { ActionButtonProps } from '../ActionButton'

import * as SC from './styled'

export type AccordionProps = MinimalSeoBlock & {
  className?: string
  title: string
  richTextProps?: RichTextProps
  media?: IllustrationProps | VideoSingleProps
  htmlDefinitionTag?: React.ElementType
  actionButtons?: (ActionButtonProps | undefined)[]
}

const Accordion: FC<AccordionProps> = ({
  className,
  title,
  richTextProps,
  media,
  htmlTitleTag = 'dt',
  htmlDefinitionTag = 'dd',
  actionButtons,
}) => {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [heightContent, setHeightContent] = useState(250)
  const refButton = React.useRef<HTMLDivElement>(null)
  const refContent = React.useRef<HTMLDivElement>(null)

  const firstRender = useRef(true)
  const [refWrapper, animate] = useAnimate()

  // Type guard for IllustrationProps
  function isIllustrationProps(media: any): media is IllustrationProps {
    return (media as IllustrationProps).pictureProps !== undefined
  }

  // Type guard for VideoSingleProps
  function isVideoSingleProps(media: any): media is VideoSingleProps {
    return (media as VideoSingleProps).options?.videoId !== undefined
  }

  const handleHeight = () => {
    if (refContent.current) {
      setHeightContent(refContent.current.clientHeight)
    }
  }

  const handleToggle = () => {
    setOpen(!open)
    handleHeight()
  }

  const { buttonProps } = useButton(
    {
      id: `${id}-button`,
      'aria-controls': `${id}-content`,
      onPress: handleToggle,
      'aria-expanded': open,
    },
    refButton
  )

  // get height of the content on load and resize
  useEffect(() => {
    // delayed version because inside the slider it was not going great ):
    const handleHeightDelayed = () => {
      setTimeout(handleHeight, 300)
    }
    // on load
    handleHeightDelayed()
    // on resize
    window.addEventListener('resize', handleHeightDelayed)
    return () => window.removeEventListener('resize', handleHeightDelayed)
  }, [])

  // close the switch on "escape"
  useEffect(() => {
    if (open === false) return
    const handlePressEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', handlePressEscape)
    return () => {
      window.removeEventListener('keydown', handlePressEscape)
    }
  }, [open])

  // animate open/close
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    if (refWrapper.current) {
      animate(
        refWrapper.current,
        {
          height: open ? [heightContent, 'auto'] : 0,
        },
        { duration: 0.3, ease: easeInOut }
      )
    }
  }, [open, heightContent, animate, refWrapper])

  return (
    <SC.Container className={cx('Accordion', className)} data-open={open}>
      <SC.Summary
        ref={refButton}
        {...buttonProps}
        tabIndex={0}
        as={htmlTitleTag}
      >
        <SC.Label>{title}</SC.Label>
        <SC.Toggle aria-hidden>
          <SC.OpenIcon icon={Icons.plus} />
          <SC.CloseIcon icon={Icons.minus} />
        </SC.Toggle>
      </SC.Summary>
      <SC.Wrapper
        ref={refWrapper}
        as={htmlDefinitionTag}
        hidden={!open}
        id={`${id}-content`}
        $height={heightContent}
        aria-labelledby={`${id}-button`}
      >
        <SC.Content ref={refContent} data-height={heightContent}>
          <SC._RichText {...richTextProps} />
          {media && isIllustrationProps(media) && <Illustration {...media} />}
          {media && isVideoSingleProps(media) && (
            // the key is a dirty solution but quicker than transforming the video component into a forwardrefs component (whi is the proper solution)
            <VideoSingle {...media} key={open ? `▶️-${id}` : `⏯️-${id}`} />
          )}
          {actionButtons && (
            <SC.ActionButtons>
              {actionButtons.map((actionButton, index) =>
                actionButton ? (
                  <ActionButton key={index} {...actionButton} />
                ) : null
              )}
            </SC.ActionButtons>
          )}
        </SC.Content>
      </SC.Wrapper>
    </SC.Container>
  )
}

export default Accordion
